import { IPayrollSettings } from "@wingspanhq/payments/dist/interfaces";
import {
  IAccount,
  IActivity,
  IClient,
  INewUser
} from "@wingspanhq/users/dist/lib/interfaces";
import { selectorAccountFullName } from "../../../shared/selectors/selectorAccountFullName";
import { PayrollFundingMethod } from "../steps/PayrollFundingMethod";
import {
  OnboardingContext,
  OnboardingModulePayrollFundingMethod
} from "../types";
import { isUserIdsIncluded } from "../../../userFeatures/utils/isUserIdsIncluded";
import { PAYROLL_FUNDING_METHOD_MANAGED_BY_ORGANIZATION } from "../../../userFeatures/helpers";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import {
  useClientQuery,
  useQueryAccounts,
  useQueryActivities,
  useQueryUserProfile
} from "../../../query/users/queries";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  payrollSettings?: IPayrollSettings,
  activity?: IActivity,
  client?: IClient,
  isManaged?: boolean
) => {
  const isActivityComplete =
    !!activity?.events?.onboardingFundingMethodCompletedAt;

  const hasFundingSource =
    !!payrollSettings?.fundingSource ||
    !!client?.profile.defaultPaymentMethod?.accountId ||
    !!client?.profile.defaultPaymentMethod?.paymentMethodId;

  if (isManaged) {
    if (!isActivityComplete) {
      return "None";
    }

    if (!hasFundingSource) {
      return "Pending";
    }
  } else {
    if (!hasFundingSource) {
      return "None";
    }
  }

  return "Complete";
};

export const getPayrollFundingMethodStepConfig = (
  _: OnboardingModulePayrollFundingMethod,
  __: OnboardingContext,
  payrollSettings?: IPayrollSettings,
  accounts?: IAccount[],
  activity?: IActivity,
  client?: IClient,
  user?: INewUser
): FlowStep => {
  const slug = "payroll-funding-method";

  const isPayrollFundingMethodManagedByOrganization = isUserIdsIncluded(
    user,
    PAYROLL_FUNDING_METHOD_MANAGED_BY_ORGANIZATION
  );

  const status = getStatus(
    payrollSettings,
    activity,
    client,
    isPayrollFundingMethodManagedByOrganization
  );

  const account = accounts?.find(
    account =>
      account.accountId === payrollSettings?.fundingSource?.fundingSourceId
  );

  const accountText = account ? selectorAccountFullName(account) + " " : "";

  return {
    title: "Payroll funding method",
    slug,
    component: PayrollFundingMethod,

    status,
    result: {
      Complete: {
        title: "Your payroll funding method was added",
        description: `The payroll funding method ${accountText}was added. Click continue for the next step.`
      },
      Pending: {
        type: "card",
        title: "Awaiting payroll funding method",
        description:
          "A payroll funding method must to added to send scheduled payments.  Any incomplete setup tasks will appear on your “Get started” homepage. Incomplete tasks will delay your ability to send payments. Click continue for the next step."
      }
    }
  };
};

export const usePayrollFundingMethodStep: UseCreateOnboardingStep<
  OnboardingModulePayrollFundingMethod
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "payroll_funding_method"
  );
  const userId = useUserId();
  const queryPayrollSettings = useQueryPayrollSettings(userId, config);
  const queryAccounts = useQueryAccounts(config);
  const queryActivity = useQueryActivities(userId, config);
  const queryClient = useClientQuery(userId, config);
  const queryUser = useQueryUserProfile(userId, config);

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getPayrollFundingMethodStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryPayrollSettings,
        queryAccounts,
        queryActivity,
        queryClient,
        queryUser
      ]
    });
};
